import * as React from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import type { LocationDto } from "../../models/Locations";
import { countDescendants } from "./locationTree.utils";
import { getApiErrorMessage } from "./apiError.utils";

type LocationDeleteDialogProps = {
    location: LocationDto | null;
    locations: LocationDto[];
    onCancel: () => void;
    onConfirm: () => Promise<void>;
};

export default function LocationDeleteDialog({ location, locations, onCancel, onConfirm }: LocationDeleteDialogProps) {
    const [deleting, setDeleting] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState("");
    const descendantCount = location ? countDescendants(locations, location.locationId) : 0;
    const blockedByChildren = descendantCount > 0;

    // Reset any stale error/loading state whenever a different location is targeted (or the dialog closes).
    React.useEffect(() => {
        setDeleteError("");
        setDeleting(false);
    }, [location?.locationId]);

    const handleConfirm = async () => {
        setDeleting(true);
        setDeleteError("");
        try {
            await onConfirm();
        } catch (e: any) {
            setDeleteError(getApiErrorMessage(e, "Unable to delete this location."));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Dialog open={location != null} onClose={deleting ? undefined : onCancel}>
            <DialogTitle>Remove "{location?.locationName}"?</DialogTitle>
            <DialogContent>
                {blockedByChildren ? (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        This location has {descendantCount} sub-location{descendantCount === 1 ? "" : "s"} beneath it
                        and can't be deleted until they're moved or removed first.
                    </Alert>
                ) : (
                    <DialogContentText>This can't be undone.</DialogContentText>
                )}
                {deleteError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {deleteError}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={deleting}>
                    {blockedByChildren ? "Close" : "Cancel"}
                </Button>
                {!blockedByChildren && (
                    <Button color="error" variant="contained" onClick={handleConfirm} disabled={deleting}>
                        Remove
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
